import * as React from 'react'
import type { OutputTable, TabularConfigDraft } from '@axilon-apps/domains-ontology-ui-state'
import { Button } from '../../../components/ui/button'
import { Dialog, DialogContent, DialogFooter } from '../../../components/ui/dialog'
import { Switch } from '../../../components/ui/switch'
import { Textarea } from '../../../components/ui/textarea'
import { normaliseContract, sameContract, validateContract } from './ContractEditorDialog'
import { ContractTablesEditor } from './ContractTablesEditor'
import { NOTE_MAX_CHARS } from './InstructionDialog'
import {
  EDITOR_DIALOG_CLASS,
  EDITOR_LABEL_CLASS,
  EditorDialogHeader,
  EditorError,
  EditorProblems
} from './editorChrome'

export interface ReviseDraftSubmit {
  note: string
  /** Present only when the reviewer changed the draft's tables. */
  tables?: OutputTable[]
}

/**
 * Send a draft back to the builder with a note, and optionally an edited
 * contract: it starts from this draft, not from scratch, and proposes the
 * next one.
 */
export function ReviseDraftDialog({
  open,
  onOpenChange,
  draft,
  parserKind,
  loading,
  error,
  onSubmit
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  draft: TabularConfigDraft
  parserKind: 'script' | 'agent'
  loading: boolean
  error?: string
  onSubmit: (input: ReviseDraftSubmit) => void
}) {
  const initial = draft.output.tables
  const [note, setNote] = React.useState('')
  const [editContract, setEditContract] = React.useState(false)
  const [tables, setTables] = React.useState<OutputTable[]>(initial)
  const initialRef = React.useRef(initial)
  initialRef.current = initial
  React.useEffect(() => {
    if (open) {
      setNote('')
      setEditContract(false)
      setTables(initialRef.current)
    }
  }, [open])

  const problems = editContract ? validateContract(tables) : []
  const changedTables = editContract && !sameContract(tables, initial)
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={`${EDITOR_DIALOG_CLASS} ${editContract ? 'max-h-[92vh] sm:max-w-5xl' : ''}`}
      >
        <EditorDialogHeader title={`Send draft v${draft.proposedVersion} back`}>
          The builder starts from this draft and proposes a new one for review
          {parserKind === 'agent' ? ', reading the example pages again with the result' : ''}. This
          draft closes; a held job, if any, waits for the new one.
        </EditorDialogHeader>
        <label className="flex flex-col gap-1">
          <span className={EDITOR_LABEL_CLASS}>What should the builder do differently?</span>
          <Textarea
            aria-label="Revision note"
            className="min-h-20 text-xs md:text-xs"
            placeholder="e.g. keep the old header name as an alias too"
            maxLength={NOTE_MAX_CHARS}
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </label>
        <label className="flex items-center gap-2 text-xs text-ink-2">
          <Switch
            aria-label="Also edit the contract"
            checked={editContract}
            onCheckedChange={setEditContract}
            data-testid="revise-edit-contract"
          />
          Also edit the contract
        </label>
        {editContract ? <ContractTablesEditor tables={tables} onChange={setTables} /> : null}
        <EditorProblems problems={problems} testId="revise-contract-problems" />
        <EditorError error={error} />
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={loading || !note.trim() || problems.length > 0}
            onClick={() =>
              onSubmit({
                note: note.trim(),
                ...(changedTables ? { tables: normaliseContract(tables) } : {})
              })
            }
          >
            Send back
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
